import {
  differenceInHours,
  formatDate,
  intervalToRestrictiveDuration,
  isAfter,
  isBefore,
  isSameDay,
} from 'vtils/date'
import { getServerDate } from './get_server_date'

export type CountdownOptions<T extends CountdownInfo> = {
  startDate: Date
  endDate: Date
  transformInfo?: (info: CountdownInfo) => T
  onChange?: (info: T) => any
}
export type CountdownStatus = 'pending' | 'running' | 'ended'
export type CountdownDiff = {
  h: string
  i: string
  s: string
}
export type CountdownDiffDays = {
  d: string
  h: string
}
export type CountdownInfo = {
  /** 是否今天 */
  isToday: boolean
  /** 上个状态 */
  prevStatus?: CountdownStatus
  /** 当前状态 */
  status: CountdownStatus
  /** 待开始 */
  isPending: boolean
  /** 进行中 */
  isRunning: boolean
  /** 已结束 */
  isEnded: boolean
  /** 距开始时间、距结束时间、已结束时间是否超过24小时 */
  isMoreThan24Hours: boolean
  diff: CountdownDiff
  diffDays: CountdownDiffDays
  formatStart: (template: string) => string
  formatEnd: (template: string) => string
  dispose?: () => any
}

export function getCountdownInfo<T extends CountdownInfo = CountdownInfo>({
  startDate,
  endDate,
  transformInfo = v => v as any,
  onChange,
}: CountdownOptions<T>): T {
  const _start = startDate
  const _end = endDate
  const _now = getServerDate()
  const isToday: boolean = isSameDay(_now, _start)
  const status: CountdownStatus = isAfter(_now, _end)
    ? 'ended'
    : isBefore(_now, _start)
    ? 'pending'
    : 'running'
  const isPending = status === 'pending'
  const isRunning = status === 'running'
  const isEnded = status === 'ended'
  const isMoreThan24Hours =
    (isPending
      ? differenceInHours(_start, _now)
      : isRunning
      ? differenceInHours(_end, _now)
      : differenceInHours(_now, _end)) >= 24
  const _diff = isPending
    ? intervalToRestrictiveDuration(_now, _start, 'hour')
    : isRunning
    ? intervalToRestrictiveDuration(_now, _end, 'hour')
    : intervalToRestrictiveDuration(_end, _now, 'hour')
  const diff: CountdownDiff = {
    h: `${_diff.hours < 10 ? '0' : ''}${_diff.hours}`,
    i: `${_diff.minutes < 10 ? '0' : ''}${_diff.minutes}`,
    s: `${_diff.seconds < 10 ? '0' : ''}${_diff.seconds}`,
  }
  const _diffDays = isPending
    ? intervalToRestrictiveDuration(_now, _start, 'day')
    : isRunning
    ? intervalToRestrictiveDuration(_now, _end, 'day')
    : intervalToRestrictiveDuration(_end, _now, 'day')
  const diffDays: CountdownDiffDays = {
    d: `${_diffDays.days < 10 ? '0' : ''}${_diffDays.days}`,
    h: `${_diffDays.hours < 10 ? '0' : ''}${_diffDays.hours}`,
  }
  const formatStart: CountdownInfo['formatStart'] = template => {
    return formatDate(_start, template)
  }
  const formatEnd: CountdownInfo['formatEnd'] = template => {
    return formatDate(_end, template)
  }
  const baseInfo: CountdownInfo = {
    isToday,
    status,
    isPending,
    isRunning,
    isEnded,
    isMoreThan24Hours,
    diff,
    diffDays,
    formatStart,
    formatEnd,
  }
  const info = transformInfo(baseInfo)
  if (onChange) {
    let prevStatus = info.status
    const timer = setInterval(() => {
      const nextInfo = {
        ...getCountdownInfo<T>({
          startDate,
          endDate,
          transformInfo,
        }),
        prevStatus: prevStatus,
      }
      prevStatus = nextInfo.status
      onChange(nextInfo)
    }, 1000)
    info.dispose = () => clearInterval(timer)
    onChange(info as any)
  }
  return info as any
}
